import Resource from './Resource';
import type RequestBuilder from './RequestBuilder';

// Intentionally as permissive as the Tools.d.ts declaration this replaces: several callers pass
// loosely typed property maps and tightening this here would be a refactor, not a decouple.
type Property = { [attribute: string]: any };
type Filter = object;
type ExtraParams = { [key: string]: any };

type AnonymizeOptions = {
	objects: number[];
	filter: Filter;
	properties: Record<string, Record<string, string>>;
	extraParams?: ExtraParams;
};

class MultiActionsInstance {
	private resource: MultiActions;
	private customerId: number;

	constructor(resource: MultiActions, customerId: number) {
		this.resource = resource;
		this.customerId = customerId;
	}

	private execute(entity: string, body: object) {
		return this.resource._execute(this.customerId, entity, body);
	}

	anonymize(options: AnonymizeOptions) {
		let body: { [key: string]: any } = {
			entity: 'anonymous',
			nrOfIds: options.objects.length,
			objects: options.objects,
			properties: options.properties,
			filter: options.filter,
			task: 'Update',
			action: 'Anonymize'
		};

		if (options.extraParams) {
			body = Object.assign(body, options.extraParams);
		}

		return this.execute('anonymous', body);
	}

	delete(
		entity: string,
		nrOfIds: number,
		filter: Filter,
		name?: string,
		useTriggers?: boolean,
		notificationEntityNameExtension?: string
	) {
		const body: { [key: string]: any } = {
			nrOfIds,
			filter,
			properties: [{ name: 'useTriggers', value: useTriggers }],
			action: 'DeleteEntity',
			task: 'Delete'
		};

		if (name) {
			body.name = name;
		}
		if (notificationEntityNameExtension) {
			body.notificationEntityNameExtension = notificationEntityNameExtension;
		}

		return this.execute(entity, body);
	}

	updateAccount(entity: string, nrOfIds: number, properties: Property[], filter: Filter, extraParams?: ExtraParams) {
		let body: { [key: string]: any } = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateClient',
			task: 'Update'
		};

		if (extraParams) {
			body = Object.assign(body, extraParams);
		}

		return this.execute(entity, body);
	}

	createActivity(
		entity: string,
		nrOfIds: number,
		properties: Property[],
		filter: Filter,
		name?: string,
		extraParams?: ExtraParams
	) {
		let body: { [key: string]: any } = {
			nrOfIds,
			filter,
			properties,
			action: 'CreateActivity',
			task: 'Create'
		};

		if (extraParams) {
			if (extraParams.source) {
				body.properties.push({ name: 'Source', value: extraParams.source });
				extraParams.source = undefined;
			}

			body = Object.assign(body, extraParams);
		}

		if (name) {
			body.name = name;
		}

		return this.execute(entity, body);
	}

	updateContact(entity: string, nrOfIds: number, properties: Property[], filter: Filter, extraParams?: ExtraParams) {
		let body: { [key: string]: any } = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateContact',
			task: 'Update'
		};

		if (extraParams) {
			body = Object.assign(body, extraParams);
		}

		return this.execute(entity, body);
	}

	updateClientContacts(
		entity: string,
		nrOfIds: number,
		properties: Property[],
		filter: Filter,
		extraParams?: ExtraParams
	) {
		let body: { [key: string]: any } = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateClientContacts',
			task: 'Update'
		};

		if (extraParams) {
			body = Object.assign(body, extraParams);
		}

		return this.execute(entity, body);
	}

	updateActivity(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateActivity',
			task: 'Update'
		};

		return this.execute('Activity', body);
	}

	updateOrder(entity: string, nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateOrder',
			task: 'Update'
		};

		return this.execute(entity, body);
	}

	updateAppointment(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateAppointment',
			task: 'Update'
		};

		return this.execute('Appointment', body);
	}

	updateFile(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateFile',
			task: 'Update'
		};

		return this.execute('FileMetadata', body);
	}

	updateMailtemplate(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateMailTemplate',
			task: 'Update'
		};

		return this.execute('MailTemplate', body);
	}

	updateForm(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateForm',
			task: 'Update'
		};

		return this.execute('Form', body);
	}

	updateMailCampaign(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'UpdateMailCampaign',
			task: 'Update'
		};

		return this.execute('MailCampaign', body);
	}

	// Unlike every other method here, this one is handed an unbuilt RequestBuilder and builds it itself.
	excludeContactsSocialEvent(nrOfIds: number, properties: Property[], filter: RequestBuilder) {
		const body = {
			nrOfIds,
			filter: filter.build(),
			action: 'ExcludeFromSocialEvent',
			task: 'Delete',
			properties
		};

		return this.execute('Contact', body);
	}

	// Unlike every other method here, this one is handed an unbuilt RequestBuilder and builds it itself.
	checkInContactsSocialEvent(nrOfIds: number, properties: Property[], filter: RequestBuilder) {
		const body = {
			nrOfIds,
			filter: filter.build(),
			action: 'CheckInToSocialEvent',
			task: 'Update',
			properties
		};

		return this.execute('Contact', body);
	}

	updateTicket(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			action: 'UpdateTicket',
			task: 'Update',
			properties
		};

		return this.execute('Ticket', body);
	}

	updateProjectPlan(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			action: 'UpdateProjectPlan',
			task: 'Update',
			properties
		};

		return this.execute('ProjectPlan', body);
	}

	prospecting(entity: string, nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			properties,
			action: 'Prospecting',
			task: 'Update'
		};

		return this.execute(entity, body);
	}

	updateComment(nrOfIds: number, properties: Property[], filter: Filter) {
		const body = {
			nrOfIds,
			filter,
			action: 'UpdateComment',
			task: 'Update',
			properties,
			skipNotification: true
		};

		return this.execute('Comment', body);
	}
}

class MultiActions extends Resource {
	constructor() {
		// Every request overrides _url with `<customerId>/multi`, but Resource requires a base url.
		// No notificationTitle: the Angular resource fired no notifications, callers fire their own.
		super('multi');
	}

	customer(customerId: number) {
		return new MultiActionsInstance(this, customerId);
	}

	_execute(customerId: number, entity: string, body: object): Promise<any> {
		return this._postRequest(entity, body, {
			_url: `${customerId}/multi`,
			methodName: 'execute'
		}).then((res: { data: any }) => res.data);
	}
}

export type MultiActionsCustomer = MultiActionsInstance;

export default new MultiActions();
